import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, SignOut as ExitIcon, Trash, X, UserList } from '@phosphor-icons/react';

export default function VisitorLogPage() {
  const { api } = useAuth();
  const [visitors, setVisitors] = useState([]);
  const [todayStats, setTodayStats] = useState({ total: 0, currently_inside: 0 });
  const [showForm, setShowForm] = useState(false);
  const [dateFilter, setDateFilter] = useState(new Date().toISOString().slice(0, 10));
  const [form, setForm] = useState({ name: '', phone: '', purpose: 'guest', host: '', vehicle: '', notes: '' });
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [vRes, tRes] = await Promise.all([
        api.get(`/visitors?date=${dateFilter}`),
        api.get('/visitors/today')
      ]);
      setVisitors(vRes.data);
      setTodayStats(tRes.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };
  useEffect(() => { fetchData(); }, [api, dateFilter]);

  const handleSave = async (e) => {
    e.preventDefault();
    await api.post('/visitors', { ...form, date: dateFilter });
    setShowForm(false);
    setForm({ name: '', phone: '', purpose: 'guest', host: '', vehicle: '', notes: '' });
    fetchData();
  };

  const handleExit = async (visitorId) => {
    await api.put(`/visitors/${visitorId}/exit`);
    fetchData();
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="visitors-page" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="overline">Visitor Log</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Visitors</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)} data-testid="visitor-date-filter"
            className="px-3 py-2 rounded-xl border border-border bg-muted/50 text-sm outline-none" />
          <button onClick={() => setShowForm(true)} data-testid="add-visitor-btn" className="px-4 py-2 rounded-full bg-[#FF6B35] text-white btn-glow text-sm font-semibold flex items-center gap-2">
            <Plus size={16} /> Log Visitor
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="glass-card p-5">
          <p className="overline">Today Total</p>
          <p className="text-2xl font-bold mt-1">{todayStats.total}</p>
        </div>
        <div className="glass-card p-5">
          <p className="overline">Currently Inside</p>
          <p className="text-2xl font-bold mt-1 text-accent">{todayStats.currently_inside}</p>
        </div>
        <div className="glass-card p-5">
          <p className="overline">Showing</p>
          <p className="text-2xl font-bold mt-1">{visitors.length}</p>
        </div>
      </div>

      {/* Visitor List */}
      <div className="space-y-3">
        {visitors.map(v => (
          <div key={v.visitor_id} className={`rounded-xl border bg-card p-4 flex items-center justify-between gap-4 ${v.status === 'inside' ? 'border-accent/30' : 'border-border'}`} data-testid={`visitor-${v.visitor_id}`}>
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${v.status === 'inside' ? 'bg-accent/10 text-accent' : 'bg-muted text-muted-foreground'}`}>
                {v.name?.[0]?.toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-sm">{v.name}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${v.status === 'inside' ? 'bg-accent/10 text-accent' : 'bg-muted text-muted-foreground'}`}>{v.status}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-muted capitalize">{v.purpose}</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {v.phone && `${v.phone} | `}Host: {v.host || 'N/A'} | Entry: {new Date(v.entry_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                  {v.exit_time && ` | Exit: ${new Date(v.exit_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`}
                </p>
                {v.vehicle && <p className="text-xs text-muted-foreground">Vehicle: {v.vehicle}</p>}
              </div>
            </div>
            <div className="flex gap-1">
              {v.status === 'inside' && (
                <button onClick={() => handleExit(v.visitor_id)} data-testid={`exit-visitor-${v.visitor_id}`} className="px-3 py-1.5 text-xs rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500/20 flex items-center gap-1">
                  <ExitIcon size={12} /> Mark Exit
                </button>
              )}
            </div>
          </div>
        ))}
        {visitors.length === 0 && (
          <div className="text-center py-16 text-muted-foreground">
            <UserList size={48} className="mx-auto mb-3 opacity-30" />
            <p>No visitors logged for this date</p>
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowForm(false)} />
          <div className="relative bg-card border border-border rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-xl font-bold">Log Visitor</h3>
              <button onClick={() => setShowForm(false)} className="p-1 rounded-lg hover:bg-muted"><X size={20} /></button>
            </div>
            <form onSubmit={handleSave} className="space-y-4">
              <div><label className="overline block mb-2">Visitor Name</label><input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="visitor-name-input" required className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50 focus:ring-1 focus:ring-[#FF6B35]/30" /></div>
              <div><label className="overline block mb-2">Phone</label><input type="text" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="visitor-phone-input" className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none" /></div>
              <div><label className="overline block mb-2">Purpose</label>
                <select value={form.purpose} onChange={e => setForm({...form, purpose: e.target.value})} data-testid="visitor-purpose-select" className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none">
                  {['guest', 'delivery', 'vendor', 'maintenance', 'other'].map(p => <option key={p} value={p} className="capitalize">{p}</option>)}
                </select>
              </div>
              <div><label className="overline block mb-2">Host</label><input type="text" value={form.host} onChange={e => setForm({...form, host: e.target.value})} data-testid="visitor-host-input" className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none" placeholder="Who are they visiting?" /></div>
              <div><label className="overline block mb-2">Vehicle</label><input type="text" value={form.vehicle} onChange={e => setForm({...form, vehicle: e.target.value})} data-testid="visitor-vehicle-input" className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none" placeholder="Vehicle number (optional)" /></div>
              <div><label className="overline block mb-2">Notes</label><input type="text" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none" /></div>
              <button type="submit" data-testid="save-visitor-btn" className="w-full py-3 rounded-full bg-[#FF6B35] text-white btn-glow font-semibold text-sm">Log Visitor</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
